import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { USER_CONFIG } from '@the-wall/shared';

export function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const register = useAuthStore((state) => state.register);
  const error = useAuthStore((state) => state.error);
  const isLoading = useAuthStore((state) => state.isLoading);
  const clearError = useAuthStore((state) => state.clearError);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    if (
      username.length < USER_CONFIG.USERNAME_MIN_LENGTH ||
      username.length > USER_CONFIG.USERNAME_MAX_LENGTH
    ) {
      setLocalError(
        `Username must be between ${USER_CONFIG.USERNAME_MIN_LENGTH} and ${USER_CONFIG.USERNAME_MAX_LENGTH} characters`
      );
      return;
    }

    try {
      await register(email, password, username);
      navigate('/');
    } catch {
      // Error is handled by store
    }
  };

  const displayError = localError ?? error;

  return (
    <div className="flex items-center justify-center h-full p-4">
      <div className="bg-wall-surface border border-wall-border rounded-lg p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center mb-6">Join The Wall</h1>

        {displayError && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-2 rounded-sm mb-4">
            {displayError}
            <button
              onClick={() => {
                setLocalError(null);
                clearError();
              }}
              className="float-right text-red-400 hover:text-red-200"
            >
              ×
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-wall-muted mb-1">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
              minLength={USER_CONFIG.USERNAME_MIN_LENGTH}
              maxLength={USER_CONFIG.USERNAME_MAX_LENGTH}
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-wall-muted mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-wall-muted mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
              minLength={8}
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-wall-muted mb-1"
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 bg-wall-bg border border-wall-border rounded-sm focus:outline-hidden focus:ring-2 focus:ring-wall-accent"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 bg-wall-accent hover:bg-opacity-80 rounded-sm font-medium transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-wall-muted mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-wall-accent hover:underline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}
